// ── Reivindicação atômica entre abas (check-and-set por navegador) ──
// Fábrica de um "reivindicador": guarda ids já tratados em localStorage (janela de
// expiração) e serializa o check-and-set num Web Lock (`navigator.locks`, o mesmo
// árbitro entre abas que elege o líder do SSE). Retorna true só para a PRIMEIRA
// aba/handler a reivindicar o id; os demais recebem false.
//
// Sem o lock, quando a aba líder faz o fan-out do evento via BroadcastChannel todas
// as abas leem "não processado" ANTES de qualquer uma marcar (corrida TOCTOU) e cada
// uma dispara a ação — N abas → N vias (impressão) / N aceites concorrentes.
//
// Vive em módulo próprio (e não dentro de uma fatia do store) porque tanto o
// PedidosStore (aceite/preparo/comanda) quanto o ImpressaoStore (trava de saída na
// impressora) precisam dele — importar um store do outro criaria ciclo no AppStore.
// Reivindicador: função de reivindicação + um método `liberar` que devolve um id à
// janela. Usado quando a AÇÃO não se concretizou (ou avançou só parcialmente): sem
// liberar, a marca otimista seguraria o id por toda a janela e a próxima tentativa
// cairia em "já tratado (pulado)".
export type Reivindicador = ((id: number | string) => Promise<boolean>) & {
    liberar: (id: number | string) => void
}

export function criarReivindicador(
    lsKey: string,
    janelaMs: number,
    lockPrefix: string
): Reivindicador {
    const ler = (): Record<string, number> => {
        try {
            return JSON.parse(localStorage.getItem(lsKey) || "{}")
        } catch {
            return {}
        }
    }
    const marcar = (id: number | string) => {
        try {
            const mapa = ler()
            const agora = Date.now()
            mapa[String(id)] = agora
            for (const k of Object.keys(mapa)) {
                if (agora - mapa[k] > janelaMs) delete mapa[k]
            }
            localStorage.setItem(lsKey, JSON.stringify(mapa))
        } catch {
            /* ignore */
        }
    }
    const liberar = (id: number | string) => {
        try {
            const mapa = ler()
            delete mapa[String(id)]
            localStorage.setItem(lsKey, JSON.stringify(mapa))
        } catch {
            /* ignore */
        }
    }
    const decidir = (id: number | string): boolean => {
        const t = ler()[String(id)]
        if (t != null && Date.now() - t < janelaMs) return false
        marcar(id)
        return true
    }
    const reivindicar = (async (id: number | string): Promise<boolean> => {
        const locks = typeof navigator !== "undefined" ? (navigator as any).locks : undefined
        if (!locks?.request) return decidir(id) // fallback best-effort (sem Web Locks)
        try {
            return await locks.request(
                `${lockPrefix}:${String(id)}`,
                { mode: "exclusive" },
                () => decidir(id)
            )
        } catch {
            return decidir(id)
        }
    }) as Reivindicador
    reivindicar.liberar = liberar
    return reivindicar
}
